import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { showStatusNotification, showWelcomeNotification } from "../utils/notifications";
import { 
  FileText, 
  Plus, 
  Clock, 
  CheckCircle, 
  XCircle, 
  DollarSign, 
  TrendingUp,
  LogOut,
  BookOpen,
  ChevronRight,
  Loader2,
  Building2
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_small-biz-loans/artifacts/ojg09jbh_logo.png";

const CustomerDashboard = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const notifiedRef = useRef(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Check for status updates when applications load
  useEffect(() => {
    if (!loading && applications.length > 0 && user && !notifiedRef.current) {
      notifiedRef.current = true;
      
      // Check for any recently updated applications with decisions
      const recentDecisions = applications.filter(app => {
        const isDecided = ['approved', 'rejected'].includes(app.status);
        const updatedRecently = app.updated_at && 
          (new Date() - new Date(app.updated_at)) < 24 * 60 * 60 * 1000; // Within 24 hours
        return isDecided && updatedRecently;
      });

      if (recentDecisions.length > 0) {
        // Show notification for the most recent decision
        const latest = recentDecisions[0];
        showStatusNotification(latest.status, latest.application_id);
      } else if (applications.some(a => a.status === 'pending' || a.status === 'under_review')) {
        // Show welcome with pending count
        const pendingCount = applications.filter(a => 
          ['pending', 'under_review', 'recommended', 'escalated'].includes(a.status)
        ).length;
        showWelcomeNotification(user, pendingCount);
      }
    }
  }, [loading, applications, user]);

  const fetchData = async () => {
    try {
      const [appsRes, statsRes] = await Promise.all([
        axios.get(`${API}/loans/my-applications`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/stats/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setApplications(appsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Approved</Badge>;
      case "rejected":
        return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Rejected</Badge>;
      case "pending_review":
        return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Under Review</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Pending</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-12 w-12 animate-spin text-[#0A2540]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="customer-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Tzur Finances" className="h-10" />
            </Link>
            
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/products" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium">
                Loan Products
              </Link>
              <Link to="/knowledge" className="text-gray-600 hover:text-[#0A2540] transition-colors font-medium">
                Knowledge Portal
              </Link>
            </nav>
            
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600 hidden sm:block">
                Welcome, <span className="font-medium text-[#0A2540]">{user?.name}</span>
              </span>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#0A2540]">Dashboard</h1>
          <p className="text-gray-600 mt-1">Manage your loan applications and track your progress</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Applications</p>
                  <p className="text-3xl font-bold text-[#0A2540] font-mono" data-testid="total-applications">
                    {stats?.total_applications || 0}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-[#0A2540]/10 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-[#0A2540]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Loans</p>
                  <p className="text-3xl font-bold text-emerald-600 font-mono" data-testid="active-loans">
                    {stats?.active_loans || 0}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-3xl font-bold text-amber-600 font-mono" data-testid="pending-applications">
                    {stats?.pending_applications || 0}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Borrowed</p>
                  <p className="text-3xl font-bold text-[#0A2540] font-mono" data-testid="total-borrowed">
                    {formatCurrency(stats?.total_borrowed || 0)}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-lg bg-[#0A2540]/10 flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-[#0A2540]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Link to="/apply" data-testid="apply-loan-btn">
            <Card className="bg-[#0A2540] text-white hover:bg-[#0A2540]/90 transition-colors cursor-pointer h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="h-12 w-12 rounded-lg bg-white/20 flex items-center justify-center">
                  <Plus className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Apply for a Loan</h3>
                  <p className="text-white/70 text-sm">Start a new application</p>
                </div>
                <ChevronRight className="h-5 w-5 ml-auto" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/products" data-testid="view-products-btn">
            <Card className="bg-white hover:border-[#0A2540] transition-colors cursor-pointer h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-[#0A2540]">Loan Products</h3>
                  <p className="text-gray-500 text-sm">Explore our offerings</p>
                </div>
                <ChevronRight className="h-5 w-5 ml-auto text-gray-400" />
              </CardContent>
            </Card>
          </Link>

          <Link to="/knowledge" data-testid="knowledge-btn">
            <Card className="bg-white hover:border-[#0A2540] transition-colors cursor-pointer h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <BookOpen className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-[#0A2540]">Knowledge Portal</h3>
                  <p className="text-gray-500 text-sm">Learn about financing</p>
                </div>
                <ChevronRight className="h-5 w-5 ml-auto text-gray-400" />
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Applications List */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-xl text-[#0A2540]">Your Applications</CardTitle>
            <CardDescription>Track the status of your loan applications</CardDescription>
          </CardHeader>
          <CardContent>
            {applications.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No applications yet</h3>
                <p className="text-gray-500 mt-1">Start by applying for a loan to grow your business</p>
                <Link to="/apply">
                  <Button className="mt-4 bg-[#0A2540]" data-testid="first-application-btn">
                    Apply Now
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {applications.map((app) => (
                  <div
                    key={app.application_id}
                    className="p-4 border border-gray-100 rounded-lg hover:border-[#0A2540]/20 transition-colors"
                    data-testid={`application-${app.application_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-[#0A2540]" />
                        </div>
                        <div>
                          <h4 className="font-medium text-[#0A2540]">{app.loan_type}</h4>
                          <p className="text-sm text-gray-500">{app.business_name}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        <div className="text-right hidden sm:block">
                          <p className="font-mono font-semibold text-[#0A2540]">
                            {formatCurrency(app.amount)}
                          </p>
                          <p className="text-sm text-gray-500">{app.term_months} months</p>
                        </div>
                        
                        <div className="text-right">
                          {getStatusBadge(app.status)}
                          <p className="text-xs text-gray-400 mt-1">
                            {formatDate(app.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {app.status === "approved" && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">Approved Amount</span>
                          <span className="font-mono font-semibold text-emerald-600">
                            {formatCurrency(app.approved_amount || app.amount || app.amount_required || 0)}
                          </span>
                        </div>
                        {app.interest_rate && (
                          <div className="flex items-center justify-between text-sm mt-1">
                            <span className="text-gray-500">Interest Rate</span>
                            <span className="font-mono font-semibold text-[#0A2540]">
                              {app.interest_rate}%
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {app.risk_score && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-gray-500">Risk Score</span>
                          <span className="font-mono font-semibold text-[#0A2540]">
                            {app.risk_score}/100
                          </span>
                        </div>
                        <Progress value={app.risk_score} className="h-2" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CustomerDashboard;
